"use client"

import { useEffect, useRef, useState } from "react"
import { DataImportCrmSource } from "@/schema/data-import"
import { Button } from "@ui/shad"
import { AlertCircle, Loader2, Upload } from "lucide-react"
import { toast } from "sonner"

import { useFetchDataImportProgress, useSubmitTwoFactorCode, useUploadFileToImport } from "@/hooks/api/data-imports"

import { TwoFactorPrompt } from "./two-factor-prompt"

interface ExtractionProgressProps {
  dataImportId: string
  sourceType: DataImportCrmSource
  onComplete: () => void
}

export function ExtractionProgress({ dataImportId, sourceType, onComplete }: ExtractionProgressProps) {
  const { data } = useFetchDataImportProgress(dataImportId, {
    refetchInterval: 2000,
  })
  const uploadFile = useUploadFileToImport(dataImportId)
  const submitCode = useSubmitTwoFactorCode(dataImportId)
  const [manualFile, setManualFile] = useState<File | null>(null)
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const acceptedExtensions = sourceType === "redtail" ? [".sql", ".zip"] : [".json", ".zip"]
  const acceptedExtensionsLabel = acceptedExtensions.join(" or ")
  const acceptedExtensionsValue = acceptedExtensions.join(",")

  // Auto-advance when extraction completes or status has moved past extraction
  useEffect(() => {
    if (data?.status === "ready_for_review" || data?.status === "completed" || data?.status === "importing") {
      onComplete()
    }
  }, [data?.status, onComplete])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    const fileName = selectedFile.name.toLowerCase()
    if (!acceptedExtensions.some((extension) => fileName.endsWith(extension))) {
      toast.error(`Please select a ${acceptedExtensionsLabel} file`)
      return
    }

    setManualFile(selectedFile)
  }

  const handleManualUpload = () => {
    if (!manualFile) return

    uploadFile.mutate(manualFile, {
      onSuccess: () => {
        setManualFile(null)
      },
    })
  }

  const handleOtpSubmit = (code: string) => {
    setTwoFactorError(null)
    submitCode.mutate(code, {
      onError: (error) => {
        setTwoFactorError(error.message || "Verification failed. Please try again.")
      },
    })
  }

  const isFailed = data?.status === "failed"
  const isAwaiting2FA = data?.status === "awaiting_2fa"
  const isScraping = data?.status === "scraping"

  const statistics = data?.statistics ?? {}
  const households = statistics.households ?? 0
  const people = statistics.people ?? 0
  const hasFoundRecords = households > 0

  // Build list of association counts (only show non-zero values)
  const associationCounts = [
    { label: "businesses", count: statistics.businesses ?? 0 },
    { label: "tasks", count: statistics.tasks ?? 0 },
    { label: "notes", count: statistics.notes ?? 0 },
    { label: "meetings", count: statistics.events ?? 0 },
    { label: "opportunities", count: statistics.opportunities ?? 0 },
  ].filter((item) => item.count > 0)

  const associationSummary = associationCounts.map((item) => `${item.count.toLocaleString()} ${item.label}`).join(", ")

  if (isFailed) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12">
        <AlertCircle className="text-red-accent h-16 w-16" />

        <div className="text-center">
          <h3 className="text-lg font-medium">Auto-download failed</h3>
          <p className="text-muted-foreground mt-1">
            Something went wrong while downloading your data from {sourceType === "redtail" ? "Redtail" : "Wealthbox"}.
          </p>
        </div>

        <div className="w-full max-w-sm">
          <p className="text-muted-foreground mb-3 text-center text-sm">
            You can manually upload your export file to continue.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedExtensionsValue}
            onChange={handleFileChange}
            className="hidden"
          />
          <div
            className={`flex cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors ${
              manualFile ? "border-primary bg-primary/5" : "hover:border-primary"
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="text-muted-foreground h-8 w-8" />
            {manualFile ? (
              <div className="text-center">
                <p className="font-medium">{manualFile.name}</p>
                <p className="text-muted-foreground text-sm">{(manualFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="font-medium">Click to upload</p>
                <p className="text-muted-foreground text-sm">Select your {acceptedExtensionsLabel} export file</p>
              </div>
            )}
          </div>

          {manualFile && (
            <Button className="mt-4 w-full" onClick={handleManualUpload} disabled={uploadFile.isPending}>
              {uploadFile.isPending && <Loader2 className="animate-spin" />}
              {uploadFile.isPending ? "Uploading..." : "Upload and continue"}
            </Button>
          )}
        </div>
      </div>
    )
  }

  if (isAwaiting2FA) {
    return (
      <TwoFactorPrompt
        sourceType={sourceType}
        isPending={submitCode.isPending}
        error={twoFactorError}
        onSubmit={handleOtpSubmit}
      />
    )
  }

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12">
      <Loader2 className="text-muted-foreground h-16 w-16 animate-spin" />

      <div className="text-center">
        <h3 className="text-lg font-medium">
          {isScraping
            ? `Downloading data from ${sourceType === "redtail" ? "Redtail" : "Wealthbox"}`
            : "Processing your data"}
        </h3>
        <p className="text-muted-foreground mt-1">
          {isScraping
            ? "This may take several minutes while we export your data"
            : statistics.households == null
              ? "Calculating counts…"
              : hasFoundRecords
                ? `Found ${households.toLocaleString()} households, ${people.toLocaleString()} people`
                : "Extracting records..."}
        </p>
        {!isScraping && associationSummary && (
          <p className="text-muted-foreground mt-1 text-sm">{associationSummary}</p>
        )}
      </div>

      <p className="text-muted-foreground mt-4 text-xs">
        {isScraping
          ? "You can close this dialog — the export will continue in the background"
          : "This may take a few minutes for large files"}
      </p>
    </div>
  )
}
